import React from "react";
import { connect } from "react-redux";
import { Button } from "../../Button";
import { Select, Input } from "../../Input";
import { Modal } from "../../Modal";
import toastMessage from "../../../utils/toastMessage";
import { getDefaultConfig, resolveConfig, loadConfigAsync, persistConfig, clearConfig, DEFAULT_MONTHLY_PLAN } from "./abcpConfig";
import { DAILY_PLAN_2026 } from "./dailyPlan2026";
import centerQuery from "../../../utils/queries/bloodCenterQuery";
import centerSiteQuery from "../../../utils/queries/bloodCenterSiteQuery";
import { getStorage } from "../../../utils/storage";
import fetchAbcPlanning, { updateAbcPlanning } from "../../../utils/queries/abcPlanningQuery";
import rbcLogoDefault  from "../../../assets/rbc-logo.png";
import btdLogoDefault  from "../../../assets/btd-logo.png";
import bloodImgDefault from "../../../assets/blood-cover.png";

const _prepY = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => _prepY + 2 - i).map(y => ({ label: String(y), value: y }));

// RCBT centers with their API keyword matchers and short keys
const CENTER_RCBT = [
  { key: "k",  label: "KIGALI RCBT",    keywords: ["kigali"] },
  { key: "b",  label: "BUTARE RCBT",    keywords: ["butare", "huye"] },
  { key: "ko", label: "KARONGI RCBT",   keywords: ["karongi"] },
  { key: "ru", label: "RUHENGERI RCBT", keywords: ["ruhengeri", "musanze"] },
  { key: "rw", label: "RWAMAGANA RCBT", keywords: ["rwamagana"] },
];

const DAY_ABBR  = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTH_ABB3 = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Returns [{date, dayName}] for a given week number of a month
const getWeekDaysList = (monthName, weekNum, year = 2026) => {
  const monthsOrder = ["JANUARY","FEBRUARY","MARCH","APRIL","MAY","JUNE","JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER"];
  const mIdx = monthsOrder.indexOf(monthName);
  if (mIdx < 0 || weekNum < 1) return [];
  const lastDate = new Date(year, mIdx + 1, 0).getDate();
  const firstDow = new Date(year, mIdx, 1).getDay();
  const daysUntilSun = firstDow === 0 ? 0 : 7 - firstDow;
  const starts = [1];
  const ends   = [Math.min(1 + daysUntilSun, lastDate)];
  let cur = ends[0] + 1;
  while (cur <= lastDate) { starts.push(cur); ends.push(Math.min(cur + 6, lastDate)); cur += 7; }
  const i = weekNum - 1;
  if (i < 0 || i >= starts.length) return [];
  const result = [];
  for (let d = starts[i]; d <= ends[i]; d++) {
    result.push({ date: d, dayName: DAY_ABBR[new Date(year, mIdx, d).getDay()] });
  }
  return result;
};

const PAGE_OPTIONS = [
  { label: "Cover (Page 0)",          value: "cover"    },
  { label: "Contents (Page 1)",       value: "contents" },
  { label: "Foreword (Page 2)",       value: "foreword" },
  { label: "Table of Content (Page 3)", value: "toc"    },
  { label: "Divider (Page 4)",        value: "divider"  },
  { label: "Summary (Page 5)",        value: "summary"  },
  { label: "Monthly Plan (Pages 6–53)", value: "monthlyPlan" },
];

const MONTHS_ORDER = [
  "JANUARY","FEBRUARY","MARCH","APRIL","MAY","JUNE",
  "JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER",
];

// ─── Shared field wrappers ────────────────────────────────────────────────────
const sectionTitle = (text) => (
  <div style={{ fontWeight: "bold", fontSize: 13, color: "#2E75B6", marginBottom: 12, marginTop: 4, borderBottom: "2px solid #2E75B6", paddingBottom: 4 }}>
    {text}
  </div>
);

const hint = (text) => (
  <p style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>{text}</p>
);

// Helper: convert a File to base64 data-URI
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload  = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Reusable image upload row: label + preview thumbnail + upload button + remove button
const ImageUploadRow = ({ label, value, defaultSrc, onUpload, onRemove }) => {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: "bold", marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {(value || defaultSrc) ? (
          <img
            src={value || defaultSrc}
            alt={label}
            style={{ height: 52, maxWidth: 120, objectFit: "contain", border: "1px solid #dee2e6", borderRadius: 4, background: "#f8f9fa", padding: 4 }}
          />
        ) : (
          <div style={{ height: 52, width: 80, border: "1px dashed #ccc", borderRadius: 4, background: "#f8f9fa", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 10, color: "#aaa" }}>No image</span>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (file.size > 5 * 1024 * 1024) {
                toastMessage("error", "Image must be under 5 MB");
                e.target.value = "";
                return;
              }
              setUploading(true);
              const b64 = await fileToBase64(file);
              onUpload(b64);
              setUploading(false);
              e.target.value = "";
            }}
          />
          <Button
            text="Upload image"
            onPress={() => fileRef.current?.click()}
            className="btn-primary"
            isSubmitting={uploading}
          />
          {value && (
            <Button
              text={defaultSrc ? "Remove (use default)" : "Remove"}
              onPress={onRemove}
              className="btn-default"
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Cover editor ─────────────────────────────────────────────────────────────
const CoverEditor = ({ cover, onChange }) => {
  const set    = (field) => (e) => onChange("cover", { ...cover, [field]: e?.target?.value ?? e });
  const setImg = (field) => (val) => onChange("cover", { ...cover, [field]: val });
  const clrImg = (field) => ()    => onChange("cover", { ...cover, [field]: null });
  return (
    <div>
      {sectionTitle("Institution Title Lines")}
      <Input label="Title Line 1" value={cover.line1} onChange={set("line1")} />
      <Input label="Title Line 2" value={cover.line2} onChange={set("line2")} />
      <Input label="Title Line 3" value={cover.line3} onChange={set("line3")} />
      <Input label="Book Title (use \\n for line break)" value={cover.bookTitle} onChange={set("bookTitle")} />
      {sectionTitle("Cover Images")}
      {hint("Upload custom images below. Leave unchanged to use the default RBC/BTD logos and blood cover photo. Max 5 MB each.")}
      <ImageUploadRow label="Left logo (RBC)"  value={cover.logoLeft}  defaultSrc={rbcLogoDefault}  onUpload={setImg("logoLeft")}  onRemove={clrImg("logoLeft")}  />
      <ImageUploadRow label="Right logo (BTD)" value={cover.logoRight} defaultSrc={btdLogoDefault}  onUpload={setImg("logoRight")} onRemove={clrImg("logoRight")} />
      <ImageUploadRow label="Cover photo"      value={cover.coverImg}  defaultSrc={bloodImgDefault} onUpload={setImg("coverImg")}  onRemove={clrImg("coverImg")}  />
    </div>
  );
};

// ─── Contents editor ──────────────────────────────────────────────────────────
const ContentsEditor = ({ contents, onChange }) => {
  const setItem = (i, val) => {
    const items = contents.items.map((item, idx) => idx === i ? val : item);
    onChange("contents", { ...contents, items });
  };
  return (
    <div>
      {sectionTitle("Content Items")}
      {contents.items.map((item, i) => (
        <Input key={i} label={`Item ${i + 1}`} value={item} onChange={e => setItem(i, e.target.value)} />
      ))}
      {sectionTitle("Copyright Notice")}
      <Input label="Copyright text" value={contents.copyrightText} onChange={e => onChange("contents", { ...contents, copyrightText: e.target.value })} />
    </div>
  );
};

// ─── Foreword editor ──────────────────────────────────────────────────────────
const ForewordEditor = ({ foreword, onChange }) => {
  const setParagraph = (i, val) => {
    const paragraphs = foreword.paragraphs.map((p, idx) => idx === i ? val : p);
    onChange("foreword", { ...foreword, paragraphs });
  };
  const setSignatory = (i, field, val) => {
    const signatories = foreword.signatories.map((s, idx) => idx === i ? { ...s, [field]: val } : s);
    onChange("foreword", { ...foreword, signatories });
  };
  return (
    <div>
      {sectionTitle("Paragraphs")}
      {foreword.paragraphs.map((p, i) => (
        <div key={i} style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: "bold", display: "block", marginBottom: 4 }}>Paragraph {i + 1}</label>
          <textarea
            value={p}
            rows={4}
            onChange={e => setParagraph(i, e.target.value)}
            style={{ width: "100%", border: "1px solid #ccc", borderRadius: 4, padding: "6px 10px", fontSize: 12, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
          />
        </div>
      ))}
      {sectionTitle("Approval Line")}
      <Input label="Approved date text" value={foreword.approvedDate} onChange={e => onChange("foreword", { ...foreword, approvedDate: e.target.value })} />
      {sectionTitle("Signatories")}
      {foreword.signatories.map((sig, i) => (
        <div key={i} style={{ border: "1px solid #dee2e6", borderRadius: 6, padding: 12, marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: "bold", color: "#555", marginBottom: 8 }}>Signatory {i + 1}</div>
          <div className="row">
            <div className="col-6">
              <Input label="Name" value={sig.name} onChange={e => setSignatory(i, "name", e.target.value)} />
            </div>
            <div className="col-6">
              <Input label="Role (\\n for line break)" value={sig.role} onChange={e => setSignatory(i, "role", e.target.value)} />
            </div>
          </div>
          <ImageUploadRow
            label="Signature photo"
            value={sig.signatureUrl || null}
            defaultSrc={null}
            onUpload={val => setSignatory(i, "signatureUrl", val)}
            onRemove={() => setSignatory(i, "signatureUrl", null)}
          />
        </div>
      ))}
    </div>
  );
};

// ─── TOC editor ───────────────────────────────────────────────────────────────
const TOCEditor = ({ toc, onChange }) => {
  const setEntry = (i, field, val) => {
    const entries = toc.entries.map((e, idx) =>
      idx === i ? { ...e, [field]: field === "page" ? (Number(val) || 1) : val } : e
    );
    onChange("toc", { ...toc, entries });
  };
  const addEntry    = () => onChange("toc", { ...toc, entries: [...toc.entries, { title: "", page: 1 }] });
  const removeEntry = (i) => onChange("toc", { ...toc, entries: toc.entries.filter((_, idx) => idx !== i) });

  return (
    <div>
      {sectionTitle("Table of Contents Entries")}
      <div className="row mb-1">
        <div className="col-9"><small style={{ fontWeight: "bold", color: "#555" }}>Title</small></div>
        <div className="col-2"><small style={{ fontWeight: "bold", color: "#555" }}>Page #</small></div>
      </div>
      {toc.entries.map((entry, i) => (
        <div key={i} className="row mb-2 align-items-center">
          <div className="col-9">
            <input
              className="form-control form-control-sm"
              value={entry.title}
              onChange={e => setEntry(i, "title", e.target.value)}
            />
          </div>
          <div className="col-2">
            <input
              type="number"
              className="form-control form-control-sm"
              value={entry.page}
              onChange={e => setEntry(i, "page", e.target.value)}
            />
          </div>
          <div className="col-1">
            <button
              onClick={() => removeEntry(i)}
              style={{ border: "none", background: "none", color: "#dc3545", cursor: "pointer", fontSize: 16, padding: 0 }}
              title="Remove"
            >×</button>
          </div>
        </div>
      ))}
      <button
        onClick={addEntry}
        style={{ marginTop: 8, padding: "4px 14px", border: "1px dashed #aaa", borderRadius: 4, background: "none", cursor: "pointer", fontSize: 12, color: "#555" }}
      >
        + Add entry
      </button>
    </div>
  );
};

// ─── Divider editor ───────────────────────────────────────────────────────────
const DividerEditor = ({ divider, onChange }) => (
  <div>
    {sectionTitle("Section Divider Title")}
    <Input
      label="Section title (displayed centered on the divider page)"
      value={divider.sectionTitle}
      onChange={e => onChange("divider", { ...divider, sectionTitle: e.target.value })}
    />
  </div>
);

// ─── Summary helpers ──────────────────────────────────────────────────────────
const PLAN_CENTER_MAP = { k: "kigali", b: "butare", ko: "karongi", ru: "ruhengeri", rw: "rwamagana" };
const computeSummaryFromPlanData = (planData) => {
  if (!planData) return null;
  const rows = MONTHS_ORDER.map(month => {
    const weeks = planData[month] || [];
    const row = { month };
    let rcbtSess = 0, rcbtUnits = 0;
    Object.entries(PLAN_CENTER_MAP).forEach(([ck, label]) => {
      let sess = 0, units = 0;
      weeks.forEach(w => { sess += (w[ck]?.[1] || 0); units += (w[ck]?.[2] || 0); });
      row[label] = [sess, units];
      rcbtSess += sess; rcbtUnits += units;
    });
    row.rcbtTotal = [rcbtSess, rcbtUnits];
    row.rmth = [0, 0]; row.rnp = [0, 0];
    row.grand = [rcbtSess, rcbtUnits];
    return row;
  });
  const total = { kigali:[0,0], butare:[0,0], karongi:[0,0], ruhengeri:[0,0], rwamagana:[0,0],
                  rcbtTotal:[0,0], rmth:[0,0], rnp:[0,0], grand:[0,0] };
  rows.forEach(row => { Object.keys(total).forEach(k => { if (row[k]) { total[k][0] += row[k][0]; total[k][1] += row[k][1]; } }); });
  return { rows, total };
};

// ─── Summary editor ───────────────────────────────────────────────────────────
const CENTER_KEYS   = ["kigali","butare","karongi","ruhengeri","rwamagana","rcbtTotal","rmth","rnp","grand"];
const CENTER_LABELS = ["KIGALI","BUTARE","KARONGI","RUHENGERI","RWAMAGANA","TOTAL RCBTs","RMTH BB","RNP","GRAND"];

const cellInput = (value, onChange) => (
  <input
    type="number"
    value={value}
    onChange={e => onChange(Number(e.target.value) || 0)}
    style={{ width: "100%", border: "1px solid #dee2e6", borderRadius: 3, padding: "1px 4px", fontSize: 10, textAlign: "center" }}
  />
);

const SummaryEditor = ({ summary, onChange }) => {
  const setRow = (rowIdx, key, subIdx, val) => {
    const rows = summary.rows.map((r, ri) => {
      if (ri !== rowIdx) return r;
      const arr = [...r[key]];
      arr[subIdx] = val;
      return { ...r, [key]: arr };
    });
    onChange("summary", { ...summary, rows });
  };

  const setTotal = (key, subIdx, val) => {
    const total = { ...summary.total, [key]: summary.total[key].map((v, i) => i === subIdx ? val : v) };
    onChange("summary", { ...summary, total });
  };

  return (
    <div>
      {sectionTitle("Summary Table — Monthly Data")}
      {hint("Each center has two columns: Sessions and Units. Edit any cell then click Save Changes.")}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 10, minWidth: 900, width: "100%" }}>
          <thead>
            <tr>
              <th style={thStyle("#1F4E2D","#fff",{ width: 72 })}>Month</th>
              {CENTER_KEYS.map((k, i) => (
                <th key={k} colSpan={2} style={thStyle(i < 5 ? "#1F4E2D" : i === 5 ? "#375623" : i < 8 ? "#1F4E2D" : "#C55A11", "#fff")}>{CENTER_LABELS[i]}</th>
              ))}
            </tr>
            <tr>
              <th style={thStyle("#FFC000","#000")}></th>
              {CENTER_KEYS.map(k => (
                <React.Fragment key={k}>
                  <th style={thStyle("#FFC000","#000")}>Sess</th>
                  <th style={thStyle("#FFC000","#000")}>Units</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {summary.rows.map((row, rowIdx) => (
              <tr key={rowIdx} style={{ background: rowIdx % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                <td style={{ ...tdStyle(), fontWeight: "bold", whiteSpace: "nowrap" }}>{MONTHS_ORDER[rowIdx]}</td>
                {CENTER_KEYS.map(k => (
                  <React.Fragment key={k}>
                    <td style={tdStyle()}>{cellInput(row[k][0], v => setRow(rowIdx, k, 0, v))}</td>
                    <td style={tdStyle()}>{cellInput(row[k][1], v => setRow(rowIdx, k, 1, v))}</td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
            <tr style={{ background: "#70AD47", fontWeight: "bold" }}>
              <td style={{ ...tdStyle(), fontWeight: "bold" }}>TOTAL</td>
              {CENTER_KEYS.map(k => (
                <React.Fragment key={k}>
                  <td style={tdStyle()}>{cellInput(summary.total[k][0], v => setTotal(k, 0, v))}</td>
                  <td style={tdStyle()}>{cellInput(summary.total[k][1], v => setTotal(k, 1, v))}</td>
                </React.Fragment>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const thStyle = (bg, color, extra = {}) => ({
  background: bg, color, padding: "4px 6px", border: "1px solid #ccc",
  fontSize: 10, textAlign: "center", ...extra,
});
const tdStyle = () => ({
  border: "1px solid #dee2e6", padding: "2px 4px", textAlign: "center",
});

// ─── Summary read-only (computed from plan data) ──────────────────────────────
const SummaryReadOnly = ({ planData }) => {
  const computed = computeSummaryFromPlanData(planData);
  if (!computed) {
    return (
      <div>
        {sectionTitle("Summary Table — Auto-generated")}
        {hint("No planning data available. Create an ABC Planning first.")}
      </div>
    );
  }
  const { rows, total } = computed;
  return (
    <div>
      {sectionTitle("Summary Table — Auto-generated from Monthly Plan")}
      {hint("This summary is computed automatically from the Monthly Plan. To update values, edit the Monthly Plan then Save Changes.")}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 10, minWidth: 900, width: "100%" }}>
          <thead>
            <tr>
              <th style={thStyle("#1F4E2D","#fff",{ width: 72 })}>Month</th>
              {CENTER_KEYS.map((k, i) => (
                <th key={k} colSpan={2} style={thStyle(i < 5 ? "#1F4E2D" : i === 5 ? "#375623" : i < 8 ? "#1F4E2D" : "#C55A11", "#fff")}>{CENTER_LABELS[i]}</th>
              ))}
            </tr>
            <tr>
              <th style={thStyle("#FFC000","#000")}></th>
              {CENTER_KEYS.map(k => (
                <React.Fragment key={k}>
                  <th style={thStyle("#FFC000","#000")}>Sess</th>
                  <th style={thStyle("#FFC000","#000")}>Units</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} style={{ background: rowIdx % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                <td style={{ ...tdStyle(), fontWeight: "bold", whiteSpace: "nowrap" }}>{MONTHS_ORDER[rowIdx]}</td>
                {CENTER_KEYS.map(k => (
                  <React.Fragment key={k}>
                    <td style={{ ...tdStyle(), textAlign: "center" }}>{row[k]?.[0] ?? 0}</td>
                    <td style={{ ...tdStyle(), textAlign: "center" }}>{row[k]?.[1] ?? 0}</td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
            <tr style={{ background: "#70AD47", fontWeight: "bold" }}>
              <td style={{ ...tdStyle(), fontWeight: "bold" }}>TOTAL</td>
              {CENTER_KEYS.map(k => (
                <React.Fragment key={k}>
                  <td style={{ ...tdStyle(), textAlign: "center" }}>{total[k]?.[0] ?? 0}</td>
                  <td style={{ ...tdStyle(), textAlign: "center" }}>{total[k]?.[1] ?? 0}</td>
                </React.Fragment>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Returns the latest Date a site appeared in a center's plan strictly before the given day.
// Checks dailyPlan overrides first; falls back to DAILY_PLAN_2026 for year 2026.
const findLastScheduled = (centerKey, siteName, dailyPlan, planYear, targetMonthName, targetDay) => {
  const targetMonthIdx = MONTHS_ORDER.indexOf(targetMonthName);
  let lastDate = null;

  for (let mi = 0; mi <= targetMonthIdx; mi++) {
    const monthName = MONTHS_ORDER[mi];
    const dayLimit  = mi === targetMonthIdx ? targetDay - 1 : 31;

    for (let d = 1; d <= dayLimit; d++) {
      const override = dailyPlan?.[monthName]?.[d];
      const def      = planYear === 2026 ? DAILY_PLAN_2026[monthName]?.[d] : null;
      const dayData  = override || def;
      if (!dayData) continue;
      if ((dayData[centerKey] || []).includes(siteName)) {
        const candidate = new Date(planYear, mi, d);
        if (!lastDate || candidate > lastDate) lastDate = candidate;
      }
    }
  }

  return lastDate;
};

// ─── Monthly Plan editor ──────────────────────────────────────────────────────
const CK  = ["k","b","ko","ru","rw"];
const CL  = ["KIGALI","BUTARE","KARONGI","RUHENGERI","RWAMAGANA"];

const MonthlyPlanEditor = ({ monthlyPlan, onChange, dailyPlan, onChangeDailyPlan, defaultLanguage, year, noFallbackDaily }) => {
  const [selMonth, setSelMonth]           = React.useState("JANUARY");
  const [expandedWeeks, setExpandedWeeks] = React.useState({});
  const [sitesByRcbt, setSitesByRcbt]     = React.useState({});
  const [sitesLoading, setSitesLoading]   = React.useState(false);
  // Which site-picker is open: `${date}-${rcbtKey}` or null
  const [openPicker, setOpenPicker]       = React.useState(null);
  const [pickerPos, setPickerPos]         = React.useState({ top: 0, left: 0, openUp: false });
  const [siteSearch, setSiteSearch]       = React.useState("");
  // Short key of the logged-in user's assigned center, or null for admins (see all)
  const [userCenterKey, setUserCenterKey] = React.useState(null);

  // Close picker on outside click
  React.useEffect(() => {
    if (!openPicker) return;
    const handler = (e) => {
      if (!e.target.closest('[data-site-picker]') && !e.target.closest('[data-site-picker-btn]')) {
        setOpenPicker(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openPicker]);

  const planYear = year || 2026;
  const weeks = monthlyPlan[selMonth] || (noFallbackDaily ? [] : DEFAULT_MONTHLY_PLAN[selMonth]) || [];
  const visibleRcbt = userCenterKey ? CENTER_RCBT.filter(c => c.key === userCenterKey) : CENTER_RCBT;
  const visibleCK   = userCenterKey ? CK.filter(k => k === userCenterKey) : CK;
  const showBus     = !userCenterKey;

  // Load all RCBT sites from API once; also detect user's assigned center
  React.useEffect(() => {
    (async () => {
      setSitesLoading(true);
      try {
        const [centers, user] = await Promise.all([
          centerQuery(defaultLanguage, { type: "dropdown" }),
          getStorage(),
        ]);
        const rcbtIds = {};
        CENTER_RCBT.forEach(({ key, keywords }) => {
          const match = (centers || []).find(c =>
            keywords.some(kw => (c.label || "").toLowerCase().includes(kw))
          );
          if (match) rcbtIds[key] = String(match.value);
        });

        // Detect user's assigned center (null = admin / see all)
        const userCenterIds = (user?.center || []).map(c => String(c._id || c)).filter(Boolean);
        if (userCenterIds.length > 0) {
          const matched = CENTER_RCBT.find(({ key }) =>
            rcbtIds[key] && userCenterIds.includes(rcbtIds[key])
          );
          if (matched) setUserCenterKey(matched.key);
        }

        const results = await Promise.all(
          CENTER_RCBT.map(async ({ key }) => {
            if (!rcbtIds[key]) return { key, sites: [] };
            const data = await centerSiteQuery(defaultLanguage, { type: "dropdown", center: rcbtIds[key], limit: 400 });
            return { key, sites: (data || []).map(s => s.name || (s.label || "").replace(/^[^-]+ - /, "")) };
          })
        );
        const byRcbt = {};
        results.forEach(({ key, sites }) => { byRcbt[key] = sites; });
        setSitesByRcbt(byRcbt);
      } catch (_) {}
      setSitesLoading(false);
    })();
  }, [defaultLanguage]);

  const toggleWeekExpand = (label) => {
    setExpandedWeeks(prev => ({ ...prev, [label]: !prev[label] }));
    setOpenPicker(null);
  };

  const getDayData = (date) => {
    const override = dailyPlan?.[selMonth]?.[date];
    if (override) return override;
    if (noFallbackDaily) return { holiday: null, noCol: [], sess: {}, units: {}, k: [], b: [], ko: [], ru: [], rw: [] };
    const def = DAILY_PLAN_2026[selMonth]?.[date];
    return def ? JSON.parse(JSON.stringify(def)) : { holiday: null, noCol: [], sess: {}, units: {}, k: [], b: [], ko: [], ru: [], rw: [] };
  };

  // Directly update a single field on a day and auto-save
  const updateDayField = (date, field, val) => {
    const current = getDayData(date);
    const updated  = { ...current, [field]: val };
    const monthPlan = { ...(dailyPlan?.[selMonth] || {}), [date]: updated };
    onChangeDailyPlan({ ...(dailyPlan || {}), [selMonth]: monthPlan });
  };

  const updRow = (ri, field, si, val) => {
    const nw = weeks.map((w, i) => {
      if (i !== ri) return w;
      const arr = [...(w[field] || [0,0,0])];
      arr[si] = val;
      return { ...w, [field]: arr };
    });
    onChange({ ...monthlyPlan, [selMonth]: nw });
  };

  const updNames = (ri, field, val) => {
    const nw = weeks.map((w, i) => {
      if (i !== ri) return w;
      const names = val.split(/,\s*/).map(s => s.trim()).filter(Boolean);
      const arr = [...(w[field] || [0, 0, 0])];
      arr[0] = names.length;
      return { ...w, [field]: arr, [`${field}Names`]: names };
    });
    onChange({ ...monthlyPlan, [selMonth]: nw });
  };

  const updT = (ri, si, val) => {
    const nw = weeks.map((w, i) => {
      if (i !== ri) return w;
      const arr = [...(w.t || [0,0])];
      arr[si] = val;
      return { ...w, t: arr };
    });
    onChange({ ...monthlyPlan, [selMonth]: nw });
  };

  const updBus = (ri, si, val) => {
    const nw = weeks.map((w, i) => {
      if (i !== ri) return w;
      const arr = [...(w.bus || [0,0])];
      arr[si] = val;
      return { ...w, bus: arr };
    });
    onChange({ ...monthlyPlan, [selMonth]: nw });
  };

  const monthAbbr = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  // 1 week + N*3 center + 2 bus (admin only) + 2 total + 1 expand
  const TOTAL_COLS = 1 + visibleCK.length * 3 + (showBus ? 2 : 0) + 2 + 1;

  return (
    <div>
      {sectionTitle("Monthly Planned Data")}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 14 }}>
        {MONTHS_ORDER.map((m, i) => (
          <button
            key={m}
            onClick={() => { setSelMonth(m); setExpandedWeeks({}); setOpenPicker(null); }}
            style={{
              padding: "4px 10px", fontSize: 11,
              fontWeight: selMonth === m ? "bold" : "normal",
              border: selMonth === m ? "2px solid #1F4E2D" : "1px solid #ccc",
              borderRadius: 4,
              background: selMonth === m ? "#1F4E2D" : "#f8f9fa",
              color: selMonth === m ? "#fff" : "#333",
              cursor: "pointer", letterSpacing: 0.3,
            }}
          >{monthAbbr[i]}</button>
        ))}
      </div>
      {userCenterKey && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#e8f4fd", border: "1px solid #2E75B6", borderRadius: 4, padding: "3px 10px", marginBottom: 10, fontSize: 11 }}>
          <span style={{ color: "#2E75B6", fontWeight: "bold" }}>
            {CENTER_RCBT.find(c => c.key === userCenterKey)?.label} view
          </span>
          <span style={{ color: "#555" }}>— editing your center only</span>
        </div>
      )}
      {hint(`${selMonth} — Edit weekly totals here. Click ▶ on a WEEK row to expand and edit per-day planned sites, sessions, and units.`)}
      {sitesLoading && <p style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>Loading sites from server...</p>}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 10, width: "100%" }}>
          <thead>
            <tr>
              <th style={thStyle("#1F4E2D","#fff",{ width: 70 })}>Week</th>
              {visibleCK.map((k, i) => {
                const ci = CK.indexOf(k);
                return <th key={k} colSpan={3} style={thStyle("#1F4E2D","#fff")}>{CL[ci]}</th>;
              })}
              {showBus && <th colSpan={2} style={thStyle("#375623","#fff")}>Bus</th>}
              <th colSpan={2} style={thStyle("#C55A11","#fff")}>TOTAL</th>
              <th style={thStyle("#444","#fff",{ width: 28 })} title="Expand per-day plan"></th>
            </tr>
            <tr>
              <th style={thStyle("#FFC000","#000")}></th>
              {visibleCK.map(k => (
                <React.Fragment key={k}>
                  <th style={thStyle("#FFC000","#000",{fontSize:9})} title="Weekly site names (comma-separated)">Sites</th>
                  <th style={thStyle("#FFC000","#000",{fontSize:9})}>Sess</th>
                  <th style={thStyle("#FFC000","#000",{fontSize:9})}>Units</th>
                </React.Fragment>
              ))}
              {showBus && <>
                <th style={thStyle("#FFC000","#000",{fontSize:9})}>Sess</th>
                <th style={thStyle("#FFC000","#000",{fontSize:9})}>Units</th>
              </>}
              <th style={thStyle("#FFC000","#000",{fontSize:9})}>Sess</th>
              <th style={thStyle("#FFC000","#000",{fontSize:9})}>Units</th>
              <th style={thStyle("#FFC000","#000",{fontSize:9})}></th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((row, ri) => {
              const isFixed   = row.label === "FIXED";
              const weekMatch = row.label.match(/WEEK (\d+)/);
              const weekNum   = weekMatch ? Number(weekMatch[1]) : null;
              const isExpanded = !isFixed && !!expandedWeeks[row.label];
              const weekDays   = isExpanded && weekNum ? getWeekDaysList(selMonth, weekNum, planYear) : [];

              return (
                <React.Fragment key={ri}>
                  {/* Weekly aggregate row */}
                  <tr style={{ background: isFixed ? "#E2EFDA" : isExpanded ? "#e8f4fd" : ri % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                    <td style={{ ...tdStyle(), fontWeight: "bold", whiteSpace: "nowrap", fontSize: 9 }}>{row.label}</td>
                    {visibleCK.map(k => (
                      <React.Fragment key={k}>
                        <td style={{ ...tdStyle(), minWidth: 80 }}>
                          <input
                            type="text"
                            value={(row[`${k}Names`] || []).join(", ")}
                            onChange={e => updNames(ri, k, e.target.value)}
                            placeholder={String(row[k]?.[0] ?? 0)}
                            title="Weekly site names (comma-separated)"
                            style={{ width: "100%", border: "1px solid #dee2e6", borderRadius: 3, padding: "1px 4px", fontSize: 8, boxSizing: "border-box" }}
                          />
                        </td>
                        <td style={tdStyle()}>{cellInput(row[k]?.[1] ?? 0, v => updRow(ri, k, 1, v))}</td>
                        <td style={tdStyle()}>{cellInput(row[k]?.[2] ?? 0, v => updRow(ri, k, 2, v))}</td>
                      </React.Fragment>
                    ))}
                    {showBus && (isFixed ? (
                      <>
                        <td style={tdStyle()}>{cellInput(row.bus?.[0] ?? 0, v => updBus(ri, 0, v))}</td>
                        <td style={tdStyle()}>{cellInput(row.bus?.[1] ?? 0, v => updBus(ri, 1, v))}</td>
                      </>
                    ) : (
                      <>
                        <td style={{ ...tdStyle(), background: "#f0f0f0", color: "#aaa", textAlign: "center" }}>—</td>
                        <td style={{ ...tdStyle(), background: "#f0f0f0", color: "#aaa", textAlign: "center" }}>—</td>
                      </>
                    ))}
                    <td style={tdStyle()}>{cellInput(row.t?.[0] ?? 0, v => updT(ri, 0, v))}</td>
                    <td style={tdStyle()}>{cellInput(row.t?.[1] ?? 0, v => updT(ri, 1, v))}</td>
                    <td style={{ ...tdStyle(), textAlign: "center", padding: "1px 2px" }}>
                      {!isFixed && (
                        <button
                          onClick={() => toggleWeekExpand(row.label)}
                          title={isExpanded ? "Collapse per-day plan" : "Expand to edit per-day planned sites"}
                          style={{
                            border: "none", background: isExpanded ? "#2E75B6" : "#e9ecef",
                            color: isExpanded ? "#fff" : "#555",
                            borderRadius: 3, cursor: "pointer", padding: "2px 5px", fontSize: 10,
                          }}
                        >{isExpanded ? "▼" : "▶"}</button>
                      )}
                    </td>
                  </tr>

                  {/* Expanded per-day inline-editable table */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={TOTAL_COLS} style={{ padding: 0, borderLeft: "3px solid #2E75B6", borderRight: "1px solid #ccc", borderBottom: "2px solid #2E75B6" }}>
                        <div style={{ background: "#f5f9ff", padding: "8px 10px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: "bold", color: "#2E75B6" }}>
                              {row.label} — {selMonth} per-day plan
                              {sitesLoading && <span style={{ fontWeight: "normal", color: "#888", marginLeft: 8 }}>Loading sites...</span>}
                            </span>
                            <span style={{ fontSize: 9, color: "#888" }}>Edit any cell directly — changes apply immediately to ABC Planning</span>
                          </div>
                          <div style={{ overflowX: "auto" }}>
                            <table style={{ borderCollapse: "collapse", fontSize: 9, width: "100%" }}>
                              <thead>
                                <tr>
                                  <th style={thStyle("#2E75B6","#fff",{ width: 30 })}>Date</th>
                                  <th style={thStyle("#2E75B6","#fff",{ width: 26 })}>Day</th>
                                  <th style={thStyle("#2E75B6","#fff",{ minWidth: 80 })}>Holiday / Note</th>
                                  {visibleRcbt.map(c => (
                                    <th key={c.key} colSpan={4} style={thStyle("#1F4E2D","#fff")}>{c.label.replace(" RCBT","")}</th>
                                  ))}
                                </tr>
                                <tr>
                                  <th style={thStyle("#FFC000","#000")} colSpan={3}></th>
                                  {visibleRcbt.map(c => (
                                    <React.Fragment key={c.key}>
                                      <th style={thStyle("#FFC000","#000",{ width: 20 })} title="No collection">🚫</th>
                                      <th style={thStyle("#FFC000","#000",{ minWidth: 100 })}>Sites</th>
                                      <th style={thStyle("#FFC000","#000",{ width: 30 })}>Sess</th>
                                      <th style={thStyle("#FFC000","#000",{ width: 38 })}>Units</th>
                                    </React.Fragment>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {weekDays.map(({ date, dayName }) => {
                                  const d = getDayData(date);
                                  const hasOverride = !!(dailyPlan?.[selMonth]?.[date]);
                                  const isHoliday   = !!d.holiday;
                                  const pickerKey   = (ck) => `${date}-${ck}`;
                                  const iCell = { border: "1px solid #dee2e6", padding: "1px 2px" };

                                  return (
                                    <tr key={date} style={{ background: hasOverride ? "#f0fdf4" : isHoliday ? "#fffbeb" : date % 2 === 0 ? "#fff" : "#f7f7f7" }}>
                                      {/* Date */}
                                      <td style={{ ...iCell, textAlign: "center", fontWeight: "bold", fontSize: 9 }}>{date}</td>
                                      <td style={{ ...iCell, textAlign: "center", fontSize: 8, color: "#555" }}>{dayName}</td>
                                      {/* Holiday input */}
                                      <td style={iCell}>
                                        <input
                                          type="text"
                                          value={d.holiday || ""}
                                          onChange={e => updateDayField(date, "holiday", e.target.value || null)}
                                          placeholder="—"
                                          style={{ width: "100%", border: "none", background: "transparent", fontSize: 8, fontStyle: "italic", color: "#92400e", padding: "1px 2px", outline: "1px solid transparent" }}
                                          onFocus={e => { e.target.style.outline = "1px solid #2E75B6"; e.target.style.background = "#fff"; }}
                                          onBlur={e  => { e.target.style.outline = "1px solid transparent"; e.target.style.background = "transparent"; }}
                                        />
                                      </td>
                                      {/* Per-RCBT: NoCol + Sites + Sess + Units */}
                                      {visibleRcbt.map(c => {
                                        const noCol   = (d.noCol || []).includes(c.key);
                                        const sites   = d[c.key] || [];
                                        const apiSites = sitesByRcbt[c.key] || [];
                                        const pk      = pickerKey(c.key);
                                        const pickerOpen = openPicker === pk;

                                        const toggleNoColInline = (checked) => {
                                          const nc = [...(d.noCol || [])];
                                          if (checked && !nc.includes(c.key)) nc.push(c.key);
                                          if (!checked) { const i = nc.indexOf(c.key); if (i > -1) nc.splice(i, 1); }
                                          updateDayField(date, "noCol", nc);
                                        };

                                        const toggleSiteInline = (site) => {
                                          const curr = [...sites];
                                          const idx  = curr.indexOf(site);
                                          if (idx > -1) curr.splice(idx, 1); else curr.push(site);
                                          updateDayField(date, c.key, curr);
                                        };

                                        return (
                                          <React.Fragment key={c.key}>
                                            {/* No-collection toggle */}
                                            <td style={{ ...iCell, textAlign: "center", background: noCol ? "#f3f4f6" : undefined }}>
                                              <input
                                                type="checkbox"
                                                checked={noCol}
                                                onChange={e => toggleNoColInline(e.target.checked)}
                                                title="No blood collection"
                                                style={{ cursor: "pointer" }}
                                              />
                                            </td>
                                            {/* Sites */}
                                            <td style={{ ...iCell, background: noCol ? "#f3f4f6" : undefined, position: "relative" }}>
                                              {noCol ? (
                                                <span style={{ fontSize: 8, color: "#9ca3af", fontStyle: "italic" }}>No collection</span>
                                              ) : (
                                                <div>
                                                  {/* Current sites as removable tags */}
                                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 2, marginBottom: sites.length ? 2 : 0 }}>
                                                    {sites.map(s => (
                                                      <span
                                                        key={s}
                                                        style={{ display: "inline-flex", alignItems: "center", gap: 1, background: "#1F4E2D", color: "#fff", borderRadius: 2, padding: "0px 3px", fontSize: 7 }}
                                                      >
                                                        {s}
                                                        <button
                                                          type="button"
                                                          onClick={() => toggleSiteInline(s)}
                                                          style={{ border: "none", background: "none", color: "#ccc", cursor: "pointer", fontSize: 8, lineHeight: 1, padding: "0 0 0 2px" }}
                                                          title="Remove"
                                                        >×</button>
                                                      </span>
                                                    ))}
                                                  </div>
                                                  {/* Add button opens picker — calculates position to avoid viewport clipping */}
                                                  <button
                                                    type="button"
                                                    onClick={(e) => {
                                                      if (pickerOpen) { setOpenPicker(null); return; }
                                                      const rect = e.currentTarget.getBoundingClientRect();
                                                      const PICKER_H = 280;
                                                      const spaceBelow = window.innerHeight - rect.bottom - 8;
                                                      const openUp = spaceBelow < PICKER_H && rect.top > PICKER_H;
                                                      setPickerPos({
                                                        top:  openUp ? rect.top - PICKER_H - 4 : rect.bottom + 4,
                                                        left: Math.min(rect.left, window.innerWidth - 220),
                                                        openUp,
                                                      });
                                                      setOpenPicker(pk);
                                                      setSiteSearch("");
                                                    }}
                                                    data-site-picker-btn="1"
                                                    style={{
                                                      fontSize: 7, padding: "1px 4px", cursor: "pointer",
                                                      background: pickerOpen ? "#2E75B6" : "#e9ecef",
                                                      color: pickerOpen ? "#fff" : "#555",
                                                      border: "1px solid #ccc", borderRadius: 2,
                                                    }}
                                                  >+ Add site</button>
                                                  {/* Site picker — rendered at calculated fixed position */}
                                                  {pickerOpen && (
                                                    <div data-site-picker="1" style={{
                                                      position: "fixed", zIndex: 9999,
                                                      top: pickerPos.top, left: pickerPos.left,
                                                      background: "#fff", border: "1px solid #2E75B6",
                                                      borderRadius: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.22)",
                                                      padding: 8, maxHeight: 270, overflowY: "auto", minWidth: 210,
                                                    }}>
                                                      <div style={{ position: "sticky", top: 0, background: "#fff", paddingBottom: 4, borderBottom: "1px solid #eee", marginBottom: 6 }}>
                                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                                                          <strong style={{ fontSize: 10, color: "#1F4E2D" }}>{c.label.replace(" RCBT","")} — Sites</strong>
                                                          <button type="button" onClick={() => setOpenPicker(null)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 14, color: "#888", lineHeight: 1 }}>×</button>
                                                        </div>
                                                        <input
                                                          type="text"
                                                          placeholder="Search sites..."
                                                          value={siteSearch}
                                                          onChange={e => setSiteSearch(e.target.value)}
                                                          style={{ width: "100%", fontSize: 10, padding: "3px 6px", border: "1px solid #ccc", borderRadius: 3, boxSizing: "border-box", outline: "none" }}
                                                        />
                                                      </div>
                                                      {apiSites.length === 0 && <span style={{ fontSize: 9, color: "#aaa" }}>{sitesLoading ? "Loading..." : "No sites found"}</span>}
                                                      {apiSites.filter(s => s.toLowerCase().includes(siteSearch.toLowerCase())).map(site => {
                                                        const sel = sites.includes(site);
                                                        // 60-day cooldown: find last scheduled date for this site/center
                                                        const targetDateObj = new Date(planYear, MONTHS_ORDER.indexOf(selMonth), date);
                                                        const lastSched     = findLastScheduled(c.key, site, dailyPlan, planYear, selMonth, date);
                                                        const daysAgo       = lastSched ? Math.floor((targetDateObj - lastSched) / 86400000) : null;
                                                        const cooldownLeft  = (daysAgo !== null && daysAgo < 60) ? 60 - daysAgo : 0;
                                                        // Block adding only — removal of already-selected sites is always allowed
                                                        const isBlocked     = cooldownLeft > 0 && !sel;
                                                        const lastLabel     = lastSched
                                                          ? `${lastSched.getDate()} ${MONTH_ABB3[lastSched.getMonth()]} ${lastSched.getFullYear()}`
                                                          : null;

                                                        return (
                                                          <label
                                                            key={site}
                                                            style={{
                                                              display: "flex", flexDirection: "column", gap: 1,
                                                              padding: "4px 0", borderBottom: "1px solid #f3f4f6",
                                                              cursor: isBlocked ? "not-allowed" : "pointer",
                                                            }}
                                                          >
                                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                              <input
                                                                type="checkbox"
                                                                checked={sel}
                                                                disabled={isBlocked}
                                                                onChange={() => !isBlocked && toggleSiteInline(site)}
                                                                style={{ cursor: isBlocked ? "not-allowed" : "pointer", accentColor: "#1F4E2D" }}
                                                              />
                                                              <span style={{ color: isBlocked ? "#9ca3af" : sel ? "#1F4E2D" : "#333", fontWeight: sel ? "bold" : "normal", fontSize: 10 }}>{site}</span>
                                                            </div>
                                                            {isBlocked && lastLabel && (
                                                              <div style={{ marginLeft: 20, fontSize: 8, color: "#b45309", display: "flex", gap: 3 }}>
                                                                <span>Last: {lastLabel}</span>
                                                                <span>·</span>
                                                                <span>{cooldownLeft}d remaining</span>
                                                              </div>
                                                            )}
                                                          </label>
                                                        );
                                                      })}
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </td>
                                            {/* Sess */}
                                            <td style={{ ...iCell, background: noCol ? "#f3f4f6" : undefined }}>
                                              {!noCol && (
                                                <input
                                                  type="number"
                                                  value={d.sess?.[c.key] ?? ""}
                                                  onChange={e => updateDayField(date, "sess", { ...(d.sess || {}), [c.key]: Number(e.target.value) || 0 })}
                                                  style={{ width: "100%", border: "none", background: "transparent", fontSize: 9, textAlign: "center", padding: 0, outline: "1px solid transparent" }}
                                                  onFocus={e => { e.target.style.outline = "1px solid #2E75B6"; e.target.style.background = "#fff"; }}
                                                  onBlur={e  => { e.target.style.outline = "1px solid transparent"; e.target.style.background = "transparent"; }}
                                                />
                                              )}
                                            </td>
                                            {/* Units */}
                                            <td style={{ ...iCell, background: noCol ? "#f3f4f6" : undefined }}>
                                              {!noCol && (
                                                <input
                                                  type="number"
                                                  value={d.units?.[c.key] ?? ""}
                                                  onChange={e => updateDayField(date, "units", { ...(d.units || {}), [c.key]: Number(e.target.value) || 0 })}
                                                  style={{ width: "100%", border: "none", background: "transparent", fontSize: 9, textAlign: "right", padding: 0, outline: "1px solid transparent" }}
                                                  onFocus={e => { e.target.style.outline = "1px solid #2E75B6"; e.target.style.background = "#fff"; }}
                                                  onBlur={e  => { e.target.style.outline = "1px solid transparent"; e.target.style.background = "transparent"; }}
                                                />
                                              )}
                                            </td>
                                          </React.Fragment>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Annual Plan editor (RDF / RNP pages) ────────────────────────────────────
const AnnualPlanEditor = ({ planKey, plan, onChange }) => {
  const [selSection, setSelSection] = React.useState(0);

  if (!plan || !plan.sections) {
    return <div style={{ color: "#888", fontSize: 12 }}>No plan data found — try Reset to Defaults.</div>;
  }

  const section = plan.sections[selSection] || plan.sections[0];
  const unitsLabel = plan.unitsLabel || "UNITS";

  const updateSection = (secIdx, newSection) => {
    const sections = plan.sections.map((s, i) => i === secIdx ? newSection : s);
    onChange(planKey, { ...plan, sections });
  };

  const updateTitle = (title) => onChange(planKey, { ...plan, title });

  const updateLabel = (label) => updateSection(selSection, { ...section, label });

  const updateSiteName = (siteIdx, name) => {
    const sites = section.sites.map((s, i) => i === siteIdx ? { ...s, name } : s);
    updateSection(selSection, { ...section, sites });
  };

  const toggleNoBlood = (siteIdx, qi) => {
    const site = section.sites[siteIdx];
    const rows = site.rows.map((r, i) => i === qi ? (r === null ? ["", 0, 0] : null) : r);
    const sites = section.sites.map((s, i) => i === siteIdx ? { ...site, rows } : s);
    updateSection(selSection, { ...section, sites });
  };

  const updateSiteRow = (siteIdx, qi, field, val) => {
    const site = section.sites[siteIdx];
    const rows = site.rows.map((r, i) => {
      if (i !== qi || r === null) return r;
      const arr = [...r];
      arr[field] = field === 0 ? val : (Number(val) || 0);
      return arr;
    });
    const sites = section.sites.map((s, i) => i === siteIdx ? { ...site, rows } : s);
    updateSection(selSection, { ...section, sites });
  };

  const updateEstimated = (qi, subIdx, val) => {
    const estimated = section.estimated.map((e, i) => {
      if (i !== qi) return e;
      const arr = [...e];
      arr[subIdx] = Number(val) || 0;
      return arr;
    });
    updateSection(selSection, { ...section, estimated });
  };

  const addSite = () => {
    const newSite = { name: "New site", rows: [["", 0, 0], ["", 0, 0], ["", 0, 0], ["", 0, 0]] };
    updateSection(selSection, { ...section, sites: [...section.sites, newSite] });
  };

  const removeSite = (siteIdx) => {
    const sites = section.sites.filter((_, i) => i !== siteIdx);
    updateSection(selSection, { ...section, sites });
  };

  const secTabLabel = (sec) => `${sec.months[0].slice(0,3)}–${sec.months[3].slice(0,3)}`;

  return (
    <div>
      {sectionTitle("Page Settings")}
      <Input label="Page title" value={plan.title || ""} onChange={e => updateTitle(e.target.value)} />

      {sectionTitle("Section")}
      {hint("Each section covers one quarter (3 months × 4 repetitions). Select a section tab to edit its sites.")}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {plan.sections.map((sec, i) => (
          <button
            key={i}
            onClick={() => setSelSection(i)}
            style={{
              padding: "4px 14px", fontSize: 11,
              fontWeight: selSection === i ? "bold" : "normal",
              border: selSection === i ? "2px solid #1F4E2D" : "1px solid #ccc",
              borderRadius: 4,
              background: selSection === i ? "#1F4E2D" : "#f8f9fa",
              color: selSection === i ? "#fff" : "#333",
              cursor: "pointer",
            }}
          >{secTabLabel(sec)}</button>
        ))}
      </div>

      <Input label="Section estimated-row label" value={section.label} onChange={e => updateLabel(e.target.value)} />

      <div style={{ overflowX: "auto", marginTop: 8 }}>
        <table style={{ borderCollapse: "collapse", fontSize: 10, minWidth: 700, width: "100%" }}>
          <thead>
            <tr>
              <th style={thStyle("#1F4E2D", "#fff", { width: 140 })}>Site Name</th>
              {section.months.map(m => (
                <th key={m} colSpan={3} style={thStyle("#1F4E2D", "#fff")}>{m}</th>
              ))}
              <th style={thStyle("#444", "#fff", { width: 28 })} />
            </tr>
            <tr>
              <th style={thStyle("#FFC000", "#000")} />
              {section.months.map(m => (
                <React.Fragment key={m}>
                  <th style={thStyle("#FFC000", "#000", { fontSize: 9, minWidth: 70 })}>Date</th>
                  <th style={thStyle("#FFC000", "#000", { fontSize: 9, width: 38 })}>Sess</th>
                  <th style={thStyle("#FFC000", "#000", { fontSize: 9, width: 44 })}>{unitsLabel}</th>
                </React.Fragment>
              ))}
              <th style={thStyle("#FFC000", "#000")} />
            </tr>
          </thead>
          <tbody>
            {section.sites.map((site, siteIdx) => (
              <tr key={siteIdx} style={{ background: siteIdx % 2 === 0 ? "#FFFF99" : "#fff" }}>
                <td style={tdStyle()}>
                  <input
                    value={site.name}
                    onChange={e => updateSiteName(siteIdx, e.target.value)}
                    style={{ width: "100%", border: "1px solid #dee2e6", borderRadius: 3, padding: "1px 4px", fontSize: 10, boxSizing: "border-box" }}
                  />
                </td>
                {site.rows.map((r, qi) => (
                  r === null ? (
                    <td key={qi} colSpan={3} style={{ ...tdStyle(), textAlign: "center", background: "#595959", padding: "3px 6px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                        <span style={{ fontSize: 9, color: "#ddd", fontStyle: "italic" }}>No blood collection</span>
                        <button
                          onClick={() => toggleNoBlood(siteIdx, qi)}
                          title="Enable collection for this quarter"
                          style={{ border: "1px solid #888", background: "rgba(255,255,255,0.2)", color: "#fff", borderRadius: 2, cursor: "pointer", fontSize: 9, padding: "1px 6px", lineHeight: "14px" }}
                        >+ Add</button>
                      </div>
                    </td>
                  ) : (
                    <React.Fragment key={qi}>
                      <td style={{ ...tdStyle(), padding: "2px 3px", position: "relative" }}>
                        <input
                          value={r[0]}
                          onChange={e => updateSiteRow(siteIdx, qi, 0, e.target.value)}
                          placeholder="dd/mm/yyyy"
                          style={{ width: "100%", border: "1px solid #dee2e6", borderRadius: 2, padding: "1px 16px 1px 3px", fontSize: 8, boxSizing: "border-box" }}
                        />
                        <button
                          onClick={() => toggleNoBlood(siteIdx, qi)}
                          title="Set as No blood collection"
                          style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", border: "none", background: "none", color: "#bbb", cursor: "pointer", fontSize: 11, lineHeight: 1, padding: 0 }}
                        >✕</button>
                      </td>
                      <td style={tdStyle()}>{cellInput(r[1], v => updateSiteRow(siteIdx, qi, 1, v))}</td>
                      <td style={tdStyle()}>{cellInput(r[2], v => updateSiteRow(siteIdx, qi, 2, v))}</td>
                    </React.Fragment>
                  )
                ))}
                <td style={tdStyle()}>
                  <button
                    onClick={() => removeSite(siteIdx)}
                    style={{ border: "none", background: "none", color: "#dc3545", cursor: "pointer", fontSize: 16, padding: 0, lineHeight: 1 }}
                    title="Remove site"
                  >×</button>
                </td>
              </tr>
            ))}
            <tr style={{ background: "#FFD966" }}>
              <td style={{ ...tdStyle(), fontWeight: "bold", fontSize: 9 }}>{section.label}</td>
              {section.estimated.map((e, qi) => (
                <React.Fragment key={qi}>
                  <td style={{ ...tdStyle(), background: "#f0f0f0", color: "#aaa", textAlign: "center", fontSize: 9 }}>—</td>
                  <td style={tdStyle()}>{cellInput(e[0], v => updateEstimated(qi, 0, v))}</td>
                  <td style={tdStyle()}>{cellInput(e[1], v => updateEstimated(qi, 1, v))}</td>
                </React.Fragment>
              ))}
              <td style={tdStyle()} />
            </tr>
          </tbody>
        </table>
      </div>

      <button
        onClick={addSite}
        style={{ marginTop: 8, padding: "4px 14px", border: "1px dashed #aaa", borderRadius: 4, background: "none", cursor: "pointer", fontSize: 12, color: "#555" }}
      >+ Add site</button>
    </div>
  );
};

// ─── Main class component ─────────────────────────────────────────────────────
class ABCPPreparation extends React.Component {
  state = {
    year:             2026,
    activePage:       "cover",
    config:           resolveConfig(2026),
    isDirty:          false,
    saveStatus:       null,
    showResetConfirm: false,
    activePlanData:   null,
    loadingPlan:      false,
    showGuide:        false,
  };

  componentDidMount() {
    this._loadConfig(this.state.year);
    this._loadActivePlanning(this.state.year);
    window.addEventListener('abcp-planning-updated', this._onPlanningUpdated);
  }

  componentWillUnmount() {
    window.removeEventListener('abcp-planning-updated', this._onPlanningUpdated);
  }

  componentDidUpdate(_, prevState) {
    if (prevState.year !== this.state.year) {
      this.setState({ config: resolveConfig(this.state.year), isDirty: false, saveStatus: null });
      this._loadConfig(this.state.year);
      this._loadActivePlanning(this.state.year);
    }
  }

  _loadConfig = async (year) => {
    const config = await loadConfigAsync(year);
    this.setState(prev => prev.year === year ? { config, isDirty: false, saveStatus: null } : null);
  };

  _loadActivePlanning = async (year) => {
    this.setState({ loadingPlan: true });
    try {
      const res = await fetchAbcPlanning(this.props.defaultLanguage, { limit: 100, year });
      const list = (Array.isArray(res) ? res : res?.data) || [];
      const planning = list.find(p => p.status === "approved") || list[list.length - 1] || null;
      this.setState(prev => prev.year !== year ? null : {
        activePlanData: planning ? { id: planning._id, planData: planning.planData || {} } : null,
        loadingPlan: false,
      });
    } catch (_) {
      this.setState(prev => prev.year !== year ? null : { activePlanData: null, loadingPlan: false });
    }
  };

  _onPlanningUpdated = () => this._loadActivePlanning(this.state.year);

  handlePageChange = (page, pageConfig) => {
    this.setState(prev => ({
      config:    { ...prev.config, [page]: pageConfig },
      isDirty:   true,
      saveStatus: null,
    }));
  };

  // Daily plan changes auto-save immediately so ABC Planning reflects them right away
  autoSaveDailyPlan = async (dailyPlan) => {
    const newConfig = { ...this.state.config, dailyPlan };
    this.setState({ config: newConfig, isDirty: true, saveStatus: null });
    await persistConfig(this.state.year, newConfig);
    window.dispatchEvent(new CustomEvent('abcp-config-updated'));
  };

  handleSave = async () => {
    this.setState({ saveStatus: "saving" });
    const ok = await persistConfig(this.state.year, this.state.config);
    if (this.state.activePlanData?.id) {
      try {
        await updateAbcPlanning(this.props.defaultLanguage, {
          id: this.state.activePlanData.id,
          planData: this.state.activePlanData.planData,
        });
        window.dispatchEvent(new Event('abcp-planning-updated'));
      } catch (_) {}
    }
    if (ok) {
      window.dispatchEvent(new CustomEvent('abcp-config-updated'));
      toastMessage("success", "Saved successfully.");
    } else {
      toastMessage("error", "Save failed — storage may be full.");
    }
    this.setState({ isDirty: false, saveStatus: null });
  };

  handleReset = () => {
    this.setState({ showResetConfirm: true });
  };

  confirmReset = async () => {
    this.setState({ showResetConfirm: false });
    await clearConfig(this.state.year);
    this.setState({ config: getDefaultConfig(this.state.year), isDirty: false, saveStatus: null });
    window.dispatchEvent(new CustomEvent('abcp-config-updated'));
    toastMessage("success", "Reset to factory defaults.");
  };

  render() {
    const { year, activePage, config, isDirty, saveStatus, showResetConfirm, activePlanData } = this.state;
    const editorProps = (page) => ({ [page]: config[page], onChange: this.handlePageChange });
    const effectiveMonthlyPlan = activePlanData?.planData || config.monthlyPlan;

    return (
      <>
        {/* Unsaved-changes banner */}
        {isDirty && (
          <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 6, padding: "8px 16px", marginBottom: 14, fontSize: 12, color: "#856404" }}>
            <strong>Unsaved changes</strong> — click <strong>Save Changes</strong> to apply edits to the ABC Planning report for year {year}.
          </div>
        )}

        {/* Toolbar */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ flex: "0 0 160px", minWidth: 130 }}>
            <Select
              label="Year"
              options={YEARS}
              value={YEARS.find(y => y.value === year) || null}
              onChange={v => this.setState({ year: v?.value || 2026 })}
            />
          </div>
          <div style={{ flex: "0 0 220px", minWidth: 160 }}>
            <Select
              label="Page to Edit"
              options={PAGE_OPTIONS}
              value={PAGE_OPTIONS.find(p => p.value === activePage) || null}
              onChange={v => this.setState({ activePage: v?.value || "cover" })}
            />
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <Button text="Reset to Defaults" onPress={this.handleReset} className="btn-default" />
            <Button text="Save Changes" onPress={this.handleSave} className="btn-primary" isSubmitting={saveStatus === "saving"} />
          </div>
        </div>

        {/* Editor panel */}
        <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 8, padding: "16px clamp(12px, 3vw, 24px)", maxHeight: "70vh", overflowY: "auto" }}>
          <div style={{ fontWeight: "bold", fontSize: 15, marginBottom: 16, color: "#333" }}>
            {PAGE_OPTIONS.find(p => p.value === activePage)?.label}
          </div>
          {activePage === "cover"    && <CoverEditor    cover={config.cover}       onChange={this.handlePageChange} />}
          {activePage === "contents" && <ContentsEditor contents={config.contents} onChange={this.handlePageChange} />}
          {activePage === "foreword" && <ForewordEditor foreword={config.foreword} onChange={this.handlePageChange} />}
          {activePage === "toc"      && <TOCEditor      toc={config.toc}           onChange={this.handlePageChange} />}
          {activePage === "divider"  && <DividerEditor  divider={config.divider}   onChange={this.handlePageChange} />}
          {activePage === "summary"  && <SummaryReadOnly planData={effectiveMonthlyPlan} />}
          {activePage === "monthlyPlan" && (
            <MonthlyPlanEditor
              monthlyPlan={effectiveMonthlyPlan}
              onChange={(plan) => {
                this.setState(prev => {
                  const next = {
                    config: { ...prev.config, monthlyPlan: plan },
                    isDirty: true,
                    saveStatus: null,
                  };
                  if (prev.activePlanData) {
                    next.activePlanData = { ...prev.activePlanData, planData: plan };
                  }
                  return next;
                });
              }}
              dailyPlan={config.dailyPlan}
              onChangeDailyPlan={this.autoSaveDailyPlan}
              defaultLanguage={this.props.defaultLanguage}
              year={year}
              noFallbackDaily={!!activePlanData}
            />
          )}
        </div>

        {/* Bottom save bar */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
          <Button text="Reset to Defaults" onPress={this.handleReset} className="btn-default" />
          <Button text="Save Changes" onPress={this.handleSave} className="btn-primary" isSubmitting={saveStatus === "saving"} />
        </div>

      <Modal
        show={showResetConfirm}
        handleClose={() => this.setState({ showResetConfirm: false })}
        title="Reset to Defaults"
      >
        <div className="card">
          <div className="card-body">
            <p>Reset all edits for <strong>{year}</strong> to factory defaults? This cannot be undone.</p>
          </div>
          <div className="modal-footer">
            <Button text="Cancel" className="btn-default border" onPress={() => this.setState({ showResetConfirm: false })} />
            <Button text="Reset" className="btn-primary" onPress={this.confirmReset} />
          </div>
        </div>
      </Modal>

      {/* ── Floating help button + anchored guide popover ── */}
      <div style={{ position: "fixed", bottom: 0, right: 0, margin: "1rem", zIndex: 1051 }}>
        {this.state.showGuide && (
          <div style={{
            position: "absolute", bottom: 54, right: 0,
            width: 400, maxHeight: "72vh",
            background: "#fff", borderRadius: 10,
            boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
            border: "1px solid #dee2e6",
            display: "flex", flexDirection: "column",
            overflow: "hidden",
          }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #dee2e6", background: "#f8f9fa", borderRadius: "10px 10px 0 0", flexShrink: 0 }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>How to Use ABCP Preparation</span>
              <button
                onClick={() => this.setState({ showGuide: false })}
                style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#888", lineHeight: 1, padding: 0 }}
              >×</button>
            </div>
            {/* Scrollable body */}
            <div style={{ overflowY: "auto", padding: "16px", fontSize: 13, lineHeight: 1.65 }}>

              <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)", fontWeight: 700, marginBottom: 6 }}>What is ABCP Preparation?</p>
              <p style={{ marginBottom: 16 }}>
                This page lets you customize the content of the <strong>ABC Planning Report</strong> — including cover page text, logos, foreword, table of contents, section divider, and monthly targets — before printing or sharing.
              </p>

              <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)", fontWeight: 700, marginBottom: 6 }}>Step-by-step</p>
              <ol style={{ paddingLeft: 18, marginBottom: 16 }}>
                <li>Select the <strong>Year</strong> you want to configure.</li>
                <li>Pick a <strong>Page to Edit</strong> from the dropdown.</li>
                <li>Edit the fields shown in the panel below.</li>
                <li>Click <strong>Save Changes</strong> — the ABC Planning report will immediately reflect your edits.</li>
                <li>Use <strong>Reset to Defaults</strong> to restore factory values if needed.</li>
              </ol>

              <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)", fontWeight: 700, marginBottom: 6 }}>Pages you can edit</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                {[
                  { label: "Cover",        desc: "Organisation name, book title, logos and cover photo." },
                  { label: "Contents",     desc: "Section titles shown on the Contents page." },
                  { label: "Foreword",     desc: "Foreword text, signatories, date, and signature photos." },
                  { label: "TOC",          desc: "Table of contents — section titles and page numbers." },
                  { label: "Divider",      desc: "Section A divider heading." },
                  { label: "Monthly Plan", desc: "Blood collection targets by week and center for each month." },
                ].map(({ label, desc }) => (
                  <div key={label} style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: "8px 10px" }}>
                    <strong style={{ display: "block", marginBottom: 3, fontSize: 12 }}>{label}</strong>
                    <span style={{ fontSize: 11, color: "#555" }}>{desc}</span>
                  </div>
                ))}
              </div>

              <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)", fontWeight: 700, marginBottom: 6 }}>Signature photos (Foreword page)</p>
              <ol style={{ paddingLeft: 18, marginBottom: 16 }}>
                <li>Select <strong>Foreword (Page 2)</strong> in the <em>Page to Edit</em> dropdown.</li>
                <li>Scroll to <strong>Signatories</strong> — there are three cards.</li>
                <li>Click <strong>Upload image</strong> and select a PNG/JPG of the handwritten signature (max 5 MB).</li>
                <li>Click <strong>Save Changes</strong> to persist all uploads.</li>
              </ol>

              <div style={{ background: "#fff8e1", border: "1px solid #f59e0b", borderRadius: 6, padding: "8px 12px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ fontSize: 15, lineHeight: 1.4 }}>⚠</span>
                <span style={{ fontSize: 11, color: "#92400e", lineHeight: 1.5 }}>
                  <strong>Note:</strong> Changes affect the <em>ABC Planning</em> tab globally for the selected year. Always click <strong>Save Changes</strong> before switching pages.
                </span>
              </div>

            </div>
          </div>
        )}

        <button
          title="Need help? Use this guide"
          onClick={() => this.setState(prev => ({ showGuide: !prev.showGuide }))}
          style={{
            width: 44, height: 44, borderRadius: "50%",
            backgroundColor: this.state.showGuide ? "#fff" : "var(--primary-color)",
            color: this.state.showGuide ? "var(--primary-color)" : "white",
            border: "2px solid var(--primary-color)",
            boxShadow: "rgba(0,0,0,0.28) 0px 3px 10px",
            cursor: "pointer", fontSize: 22, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            lineHeight: 1,
          }}
        >?</button>
      </div>
      </>
    );
  }
}

const mapStateToProps = state => ({ defaultLanguage: state.Language.defaultLanguage });
export default connect(mapStateToProps)(ABCPPreparation);
